import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLanding } from "../context/LandingContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { handleLandingMediaError, resolveLandingMedia } from "../utils/landingMedia";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import { safeButtonTextSx, safeTextSx, safeTitleSx, sectionTitleFontSize } from "../utils/textSafety";
import { normalizeImagePosition, scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";
import {
  spotlightLinearGradient,
  spotlightRadialGradient,
} from "../utils/spotlightVisualOwnership";

export default function DynamicSpotlightBanner() {
  const navigate = useNavigate();
  const { catalog, theme } = useLanding();
  const { productsEnabled } = useProjectConfig();
  const spotlight = catalog?.spotlight || {};

  if (spotlight.enabled !== true) return null;

  const logo = resolveLandingMedia(spotlight.logoUrl);
  const background = resolveLandingMedia(spotlight.backgroundImageUrl);
  const backgroundPosition = normalizeImagePosition(spotlight.backgroundImagePosition);
  const titleScale = segmentVisualScale(theme, "spotlight", "title");
  const imageScale = segmentVisualScale(theme, "spotlight", "image");
  const backgroundSize = spotlight.backgroundStyle?.imageFit === "cover"
    ? "cover"
    : `${Math.round(imageScale * 100)}% auto`;
  const spacingScale = segmentVisualScale(theme, "spotlight", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(spotlight.title, { xs: "2.2rem", md: "3rem" }),
    titleScale
  );
  const showCta = Boolean(spotlight.ctaLabel && spotlight.ctaUrl) &&
    (!spotlight.ctaRequiresProducts || productsEnabled);
  const hasCopy = Boolean(spotlight.eyebrow || spotlight.title || spotlight.description);
  const hasVisibleContent = Boolean(logo || hasCopy || showCta);

  if (!hasVisibleContent) return null;

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        px: { xs: 2, sm: 4, md: 8 },
        py: { xs: 4 * spacingScale, sm: 5 * spacingScale, md: 6 * spacingScale },
        background: background
          ? `${spotlightLinearGradient(spotlight.backgroundStyle, true)}, url(${background}) ${backgroundPosition}/${backgroundSize} no-repeat`
          : spotlightLinearGradient(spotlight.backgroundStyle),
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: { xs: 230, md: 360 },
          height: { xs: 230, md: 360 },
          borderRadius: "50%",
          left: 0,
          top: "50%",
          transform: "translate(-48%,-50%)",
          background: spotlightRadialGradient(spotlight.decorationLeftStyle),
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: { xs: 180, md: 300 },
          height: { xs: 180, md: 300 },
          borderRadius: "50%",
          right: 0,
          top: "50%",
          transform: "translate(45%,-50%)",
          background: spotlightRadialGradient(spotlight.decorationRightStyle),
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1180, mx: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2.25, sm: 3, md: 5 }, minWidth: 0 }}>
        {logo ? (
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: `min(78vw, ${Math.round(250 * imageScale)}px)`, md: 330 * imageScale },
              minHeight: { xs: 72 * imageScale, md: 90 * imageScale },
              bgcolor: spotlight.logoSurfaceStyle.backgroundColor,
              border: `${Number(spotlight.logoSurfaceStyle.borderWidth) || 0}px solid ${spotlight.logoSurfaceStyle.borderColor}`,
              borderRadius: `${Number(spotlight.logoSurfaceStyle.borderRadius) || 0}px`,
              px: { xs: 3, md: 4 },
              py: { xs: 2, md: 2.5 },
              boxShadow: spotlight.logoSurfaceStyle.boxShadow,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt={spotlight.logoAlt ?? ""}
              loading="lazy"
              decoding="async"
              onError={handleLandingMediaError}
              sx={{
                display: "block",
                width: "100%",
                height: { xs: 72 * imageScale, md: 90 * imageScale },
                objectFit: spotlight.logoStyle.objectFit,
                mixBlendMode: spotlight.logoStyle.mixBlendMode,
              }}
            />
          </Box>
        ) : null}

        {logo && (hasCopy || showCta) ? (
          <Box sx={{ display: { xs: "none", sm: "block" }, width: "1px", alignSelf: "stretch", bgcolor: spotlight.dividerStyle.color }} />
        ) : null}

        {hasCopy ? (
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: "100%", textAlign: { xs: "center", sm: "left" } }}>
            {spotlight.eyebrow ? <Typography data-landing-field="catalog.spotlight.eyebrow" variant="overline" sx={{ ...safeTextSx, color: spotlight.eyebrowStyle.color }}>{spotlight.eyebrow}</Typography> : null}
            {spotlight.title ? <Typography data-landing-field="catalog.spotlight.title" variant="h3" sx={{ ...safeTitleSx, fontSize: titleSize, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)", color: spotlight.titleStyle.color, textShadow: spotlight.titleStyle.textShadow }}>{capitalizeSectionTitle(spotlight.title)}</Typography> : null}
            {spotlight.description ? <Typography data-landing-field="catalog.spotlight.description" sx={{ ...safeTextSx, mt: spotlight.title || spotlight.eyebrow ? 1 : 0, color: spotlight.descriptionStyle.color, maxWidth: 620, lineHeight: 1.65, whiteSpace: "pre-line" }}>{spotlight.description}</Typography> : null}
          </Box>
        ) : null}

        {showCta ? (
          <Button
            data-landing-field="catalog.spotlight.ctaLabel"
            variant="contained"
            onClick={() => openConfiguredUrl(spotlight.ctaUrl, navigate)}
            sx={{
              ...safeButtonTextSx,
              flexShrink: 1,
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: 0, sm: 180 },
              maxWidth: { xs: "100%", sm: 280 },
              bgcolor: spotlight.ctaStyle.backgroundColor,
              color: spotlight.ctaStyle.textColor,
              border: `${Number(spotlight.ctaStyle.borderWidth) || 0}px solid ${spotlight.ctaStyle.borderColor}`,
              boxShadow: spotlight.ctaStyle.boxShadow,
              "&:hover": {
                bgcolor: spotlight.ctaStyle.hoverBackgroundColor,
                color: spotlight.ctaStyle.hoverTextColor,
                borderColor: spotlight.ctaStyle.borderColor,
                boxShadow: spotlight.ctaStyle.hoverBoxShadow,
              },
            }}
          >
            {spotlight.ctaLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
